import Folder from "@mui/icons-material/Folder";
import { Box, Button, Card, Chip, Link, Paper, Skeleton, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { CutOffTooltip } from "../../generic/components/cut-off-tooltip";
import { GradientIcon } from "../../generic/components/gradient-icon";
import { ProgramIcon } from "../../programs/components/program-icon";
import { useRecentPrograms } from "../hooks/use-recent-programs";

import type { FC } from "react";

const CHIP_HEIGHT = 32;
// Four rows of chips stay within What's new's tile beside the card, so long names that wrap onto
// more rows are cut off rather than stretch the row.
const ROWS = 4;

/** The programs most recently created Communications in, as a way back into them. */
export const RecentProgramsCard: FC = () => {
  const { data: programs, error } = useRecentPrograms();

  if (error) return null;

  return (
    <Card
      sx={{
        p: 2,
        height: "100%",
        boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Paper
          elevation={2}
          sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75 }}
        >
          <GradientIcon>
            <Folder sx={{ fontSize: 18 }} />
          </GradientIcon>
          <Typography variant="subtitle2">Recent Programs</Typography>
        </Paper>
        {Boolean(programs?.length) && (
          <Link component={RouterLink} to="/tabbed-programs" variant="subtitle2" underline="hover">
            View all
          </Link>
        )}
      </Box>

      {programs === null ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {[160, 140, 120].map((width) => (
            <Skeleton key={width} variant="rounded" width={width} height={32} />
          ))}
        </Box>
      ) : programs.length === 0 ? (
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" sx={{ textAlign: "left", mb: 3 }}>
            Programs enable you to organise your Communications into groups. Your most recently used
            Programs will appear here!
          </Typography>
          <Button
            variant="contained"
            href="/moments"
            data-analytics-id="dashboard-recent-programs-create"
          >
            Create your first Communication
          </Button>
        </Box>
      ) : (
        <Box
          sx={(theme) => ({
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            maxHeight: `calc(${ROWS} * ${CHIP_HEIGHT}px + ${ROWS - 1} * ${theme.spacing(1)})`,
            // Not hidden: focus on a chip cut off can't scroll the rest out of place.
            overflow: "clip",
          })}
        >
          {programs.map(({ id, title, colour, icon }) => (
            // A name too long for the card is cut off by the chip; the tooltip gives it in full.
            <CutOffTooltip key={id} title={title} labelSelector=".MuiChip-label">
              <Chip
                component={RouterLink}
                to={`/tabbed-programs/${id}`}
                clickable
                color="primary"
                variant="outlined"
                icon={
                  // ProgramIcon's Tooltip drops the class Chip gives its icon, so a span takes it.
                  <Box component="span" sx={{ display: "inline-flex" }}>
                    <ProgramIcon data={icon ?? undefined} fontSize="small" sx={{ color: colour }} />
                  </Box>
                }
                label={title}
                // MUI's outlined chip tucks its icon in close to the border; give it room.
                sx={{ "& .MuiChip-icon": { ml: 1, mr: -0.25 } }}
              />
            </CutOffTooltip>
          ))}
        </Box>
      )}
    </Card>
  );
};
